import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { streamMamaAfrica, resetConversation, AiUnavailableError, SignInRequiredError } from '../utils/mamaAfricaAi';
import { useAccountGate } from './AccountGate';
import { trackEvent } from '../utils/analytics';

// Afia's full screen — used by the chat launcher on the culture pages and by /afia, the page
// the event QR code opens. It is a whole screen rather than a corner widget because on a phone
// a widget is a cramped box; here the conversation owns the screen, like a messaging app.
//
// The look: night earth (warm black) lit by three soft glows — terracotta, gold and forest green,
// the colours of kente — over a faint woven diamond pattern. Type is modern (Montserrat) with
// Cinzel and Playfair for the ceremonial moments, so it reads as today's Africa, not a museum.

const AVATAR = `${process.env.PUBLIC_URL || ''}/images/afia-hero.webp`;

const STARTERS = [
  { id: 'dayname', icon: 'sun', title: 'My Akan day name', hint: 'What my birthday says about me', prompt: 'How do I find my Akan day name, and what does it mean?' },
  { id: 'names', icon: 'heart', title: 'Name a child', hint: 'Meaningful Ghanaian names', prompt: 'I want a meaningful Ghanaian name for my child. Can you suggest some with their meanings?' },
  { id: 'diaspora', icon: 'compass', title: 'Coming home', hint: 'Diaspora survival guide', prompt: 'I am from the diaspora and planning to come to Ghana. What should I know first?' },
  { id: 'ghana', icon: 'map', title: 'Life in Ghana', hint: 'Trotro, MoMo, markets', prompt: 'Give me your Ghanaian survival guide: getting around, paying with MoMo and shopping at markets.' },
  { id: 'adinkra', icon: 'star', title: 'Adinkra symbols', hint: 'Wisdom in every shape', prompt: 'Teach me three Adinkra symbols and what they mean.' },
  { id: 'store', icon: 'bag', title: 'Shop Mama Africa', hint: 'Gifts, wear and keepsakes', prompt: 'What can I buy in the Mama Africa store?' },
];

function Icon({ name, size = 22 }) {
  const common = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round', 'aria-hidden': true };
  switch (name) {
    case 'sun':
      return <svg {...common}><circle cx="12" cy="12" r="4" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M4.9 19.1 7 17M17 7l2.1-2.1" /></svg>;
    case 'heart':
      // Sankofa heart: a heart whose top curls back inward.
      return <svg {...common}><path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10Z" /><path d="M9 9.5a1.5 1.5 0 1 1 3 0M12 9.5a1.5 1.5 0 1 1 3 0" /></svg>;
    case 'compass':
      return <svg {...common}><circle cx="12" cy="12" r="9" /><path d="m15.5 8.5-2 5-5 2 2-5 5-2Z" /></svg>;
    case 'map':
      return <svg {...common}><path d="M9 4 3 6.5v13L9 17l6 2.5 6-2.5v-13L15 6.5 9 4Z" /><path d="M9 4v13M15 6.5v13" /></svg>;
    case 'star':
      // Nsoromma (star — child of the heavens).
      return <svg {...common}><path d="M12 3v18M3 12h18M5.6 5.6l12.8 12.8M18.4 5.6 5.6 18.4" /><circle cx="12" cy="12" r="3.2" /></svg>;
    case 'bag':
      return <svg {...common}><path d="M5 8h14l-1 12H6L5 8Z" /><path d="M9 8V6a3 3 0 0 1 6 0v2" /></svg>;
    case 'send':
      return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M3.4 20.4 21 12 3.4 3.6 3.3 10l12.4 2-12.4 2 .1 6.4Z" /></svg>;
    case 'close':
      return <svg {...common}><path d="M6 6l12 12M18 6 6 18" /></svg>;
    case 'new':
      return <svg {...common}><path d="M12 5v14M5 12h14" /></svg>;
    case 'stop':
      return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden><rect x="6" y="6" width="12" height="12" rx="2.5" /></svg>;
    default:
      return null;
  }
}

/** **bold**, line breaks, bullet and numbered lists — enough for how the model writes. */
function renderInline(text, keyBase) {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => (
    /^\*\*[^*]+\*\*$/.test(p)
      ? <strong key={`${keyBase}-${i}`}>{p.slice(2, -2)}</strong>
      : <React.Fragment key={`${keyBase}-${i}`}>{p}</React.Fragment>
  ));
}

function RichText({ text }) {
  const blocks = String(text || '').replace(/\r/g, '').split(/\n{2,}/);
  return blocks.map((block, b) => {
    const lines = block.split('\n').filter((l) => l.trim() !== '');
    if (!lines.length) return null;
    const bullet = lines.every((l) => /^\s*([-*•]|\d+[.)])\s+/.test(l));
    if (bullet) {
      const ordered = /^\s*\d/.test(lines[0]);
      const Tag = ordered ? 'ol' : 'ul';
      return (
        <Tag key={b} className="afx-list">
          {lines.map((l, i) => <li key={i}>{renderInline(l.replace(/^\s*([-*•]|\d+[.)])\s+/, ''), `${b}-${i}`)}</li>)}
        </Tag>
      );
    }
    const heading = lines.length === 1 && /^#{1,4}\s+/.test(lines[0]);
    if (heading) return <p key={b} className="afx-h">{renderInline(lines[0].replace(/^#{1,4}\s+/, ''), b)}</p>;
    return (
      <p key={b}>
        {lines.map((l, i) => (
          <React.Fragment key={i}>
            {i > 0 && <br />}
            {renderInline(l.replace(/^#{1,4}\s+/, ''), `${b}-${i}`)}
          </React.Fragment>
        ))}
      </p>
    );
  });
}

function Avatar({ size = 34, ring = false }) {
  return (
    <span className={ring ? 'afx-avatar afx-avatar-ring' : 'afx-avatar'} style={{ width: size, height: size }}>
      <img src={AVATAR} alt="" />
    </span>
  );
}

let idSeq = 0;
const nextId = (p) => `${p}-${Date.now()}-${idSeq++}`;

/**
 * @param {() => void} onClose
 * @param {'overlay'|'page'} variant  page = opened from /afia (a QR scan); overlay = launcher.
 */
export default function AfiaChatScreen({ onClose, variant = 'overlay' }) {
  const navigate = useNavigate();
  const { requireAccount } = useAccountGate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [streamingId, setStreamingId] = useState(null);
  const abortRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const busy = thinking || Boolean(streamingId);

  useEffect(() => () => abortRef.current?.abort(), []);

  // The screen owns the viewport while it is open.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, []);

  // Follow the conversation; the welcome screen stays at the top.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && (messages.length || thinking)) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, thinking]);

  // Grow the composer with its text, up to a few lines.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 150)}px`;
  }, [input]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && variant === 'overlay') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, variant]);

  const ask = useCallback(async (text, { appendUser = true } = {}) => {
    const clean = text.trim();
    if (!clean) return;
    if (appendUser) {
      setMessages((prev) => [...prev, { id: nextId('u'), role: 'user', text: clean }]);
    }
    setInput('');
    setThinking(true);
    trackEvent('afia_chat_message', { length: clean.length, variant });

    const replyId = nextId('a');
    abortRef.current = new AbortController();
    let streamed = '';
    try {
      await streamMamaAfrica(clean, {
        signal: abortRef.current.signal,
        onToken: (chunk) => {
          streamed += chunk;
          if (streamed === chunk) {
            setThinking(false);
            setStreamingId(replyId);
            setMessages((prev) => [...prev, { id: replyId, role: 'afia', text: chunk, streaming: true }]);
          } else {
            setMessages((prev) => prev.map((m) => (m.id === replyId ? { ...m, text: streamed } : m)));
          }
        },
      });
      setMessages((prev) => prev.map((m) => (m.id === replyId ? { ...m, streaming: false } : m)));
    } catch (error) {
      if (error.name === 'AbortError') {
        // Stopped by the visitor: keep what was written so far.
        setMessages((prev) => prev.map((m) => (m.id === replyId ? { ...m, streaming: false } : m)));
        return;
      }
      setMessages((prev) => [
        ...prev.filter((m) => m.id !== replyId),
        error instanceof SignInRequiredError
          ? { id: replyId, role: 'afia', kind: 'signin', pending: clean }
          : {
            id: replyId,
            role: 'afia',
            kind: 'error',
            text: error instanceof AiUnavailableError && error.fromServer
              ? error.message
              : 'Forgive me, I cannot reach my thoughts just now. Please try again in a moment.',
            pending: clean,
          },
      ]);
      trackEvent('afia_chat_error', { message: error.message });
    } finally {
      setThinking(false);
      setStreamingId(null);
      abortRef.current = null;
    }
  }, [variant]);

  function submit(e) {
    e?.preventDefault();
    if (busy) return;
    void ask(input);
  }

  function retry(message) {
    setMessages((prev) => prev.filter((m) => m.id !== message.id));
    void ask(message.pending, { appendUser: false });
  }

  // Sign-in happens in the pop-up over this screen; the question is sent the moment it succeeds.
  function signInAndRetry(message) {
    requireAccount('chat with Afia', () => retry(message));
  }

  function newChat() {
    abortRef.current?.abort();
    resetConversation();
    setMessages([]);
    setInput('');
    trackEvent('afia_chat_new', { variant });
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  function go(path) {
    navigate(path);
    if (variant === 'overlay') onClose?.();
  }

  const empty = messages.length === 0;

  return (
    <div className={`afx-root afx-${variant}`} role="dialog" aria-modal="true" aria-label="Chat with Afia">
      <style>{STYLES}</style>
      <div className="afx-bg" aria-hidden />

      <header className="afx-header">
        <div className="afx-kente" aria-hidden />
        <div className="afx-header-inner">
          <Avatar size={42} ring />
          <div className="afx-title">
            <div className="afx-name">Afia</div>
            <div className="afx-status"><span className="afx-dot" />Online · Heritage guide</div>
          </div>
          {!empty && (
            <button type="button" className="afx-icon-btn" onClick={newChat} aria-label="Start a new chat" title="New chat">
              <Icon name="new" size={20} />
            </button>
          )}
          <button
            type="button"
            className="afx-icon-btn"
            onClick={variant === 'page' ? () => go('/') : onClose}
            aria-label={variant === 'page' ? 'Go to the Mama Africa website' : 'Close chat'}
            title={variant === 'page' ? 'Website' : 'Close'}
          >
            <Icon name="close" size={20} />
          </button>
        </div>
      </header>

      <main ref={scrollRef} className="afx-scroll">
        <div className="afx-column">
          {empty ? (
            <section className="afx-hero">
              <div className="afx-orb">
                <span className="afx-ripple" />
                <span className="afx-ripple afx-ripple-2" />
                <span className="afx-ripple afx-ripple-3" />
                <Avatar size={112} ring />
              </div>
              <p className="afx-kicker">Mama Africa · AI heritage guide</p>
              <h1 className="afx-akwaaba">Akwaaba.</h1>
              <p className="afx-lede">
                I am <strong>Afia</strong>. Ask me about Ghanaian names and their meanings, coming home
                from the diaspora, life in Ghana, our culture, or the Mama Africa store.
              </p>
              <div className="afx-cards">
                {STARTERS.map((s, i) => (
                  <button
                    key={s.id}
                    type="button"
                    className="afx-card"
                    style={{ animationDelay: `${0.08 * i + 0.15}s` }}
                    onClick={() => { trackEvent('afia_starter', { id: s.id, variant }); void ask(s.prompt); }}
                  >
                    <span className={`afx-card-icon afx-tone-${i % 3}`}><Icon name={s.icon} /></span>
                    <span className="afx-card-text">
                      <span className="afx-card-title">{s.title}</span>
                      <span className="afx-card-hint">{s.hint}</span>
                    </span>
                  </button>
                ))}
              </div>
            </section>
          ) : (
            <div className="afx-thread">
              {messages.map((m) => (m.role === 'user' ? (
                <div key={m.id} className="afx-row afx-row-user">
                  <div className="afx-bubble afx-bubble-user">{m.text}</div>
                </div>
              ) : (
                <div key={m.id} className="afx-row afx-row-afia">
                  <Avatar size={30} />
                  {m.kind === 'signin' ? (
                    <div className="afx-bubble afx-bubble-afia afx-notice">
                      <p>Akwaaba. Sign in or create a free account and I will answer straight away, and remember our conversation.</p>
                      <button type="button" className="afx-pill" onClick={() => signInAndRetry(m)}>Sign in to chat</button>
                    </div>
                  ) : m.kind === 'error' ? (
                    <div className="afx-bubble afx-bubble-afia afx-notice">
                      <p>{m.text}</p>
                      <button type="button" className="afx-pill afx-pill-ghost" onClick={() => retry(m)}>Try again</button>
                    </div>
                  ) : (
                    <div className={`afx-bubble afx-bubble-afia${m.streaming ? ' afx-streaming' : ''}`}>
                      <RichText text={m.text} />
                    </div>
                  )}
                </div>
              )))}
              {thinking && (
                <div className="afx-row afx-row-afia">
                  <Avatar size={30} />
                  <div className="afx-bubble afx-bubble-afia afx-typing" aria-label="Afia is thinking">
                    <span /><span /><span />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      <footer className="afx-footer">
        <form className="afx-composer" onSubmit={submit}>
          <textarea
            ref={inputRef}
            rows={1}
            value={input}
            maxLength={500}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit(); }
            }}
            placeholder="Ask Afia anything…"
            aria-label="Message Afia"
          />
          {streamingId ? (
            <button type="button" className="afx-send" onClick={() => abortRef.current?.abort()} aria-label="Stop answering">
              <Icon name="stop" size={18} />
            </button>
          ) : (
            <button type="submit" className="afx-send" disabled={!input.trim() || busy} aria-label="Send">
              <Icon name="send" size={19} />
            </button>
          )}
        </form>
        <p className="afx-fineprint">
          Afia can make mistakes, so check important details.
          {variant === 'page' && <> · <button type="button" onClick={() => go('/certificate')}>Get your free name certificate</button></>}
        </p>
      </footer>
    </div>
  );
}

const STYLES = `
.afx-root {
  --gold: #C9A558; --gold-hi: #F1D58E; --gold-lo: #8B6914;
  --earth: #0F0A07; --cream: #F7EEDF; --muted: #B8A58C;
  --terra: #B5532C; --forest: #1F7A4D; --kred: #A3262A;
  position: fixed; inset: 0; z-index: 1500;
  display: flex; flex-direction: column;
  background: var(--earth); color: var(--cream);
  font-family: 'Montserrat', system-ui, sans-serif;
  animation: afxIn .35s ease-out;
  height: 100dvh;
}
.afx-bg { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
.afx-bg::before {
  content: ''; position: absolute; inset: -20%;
  background:
    radial-gradient(40% 35% at 12% 8%, rgba(181,83,44,.38), transparent 70%),
    radial-gradient(38% 32% at 92% 18%, rgba(201,165,88,.22), transparent 70%),
    radial-gradient(45% 40% at 80% 100%, rgba(31,122,77,.26), transparent 70%),
    radial-gradient(35% 30% at 0% 90%, rgba(163,38,42,.18), transparent 70%);
  filter: blur(8px);
  animation: afxDrift 22s ease-in-out infinite alternate;
}
.afx-bg::after {
  content: ''; position: absolute; inset: 0; opacity: .07;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48' viewBox='0 0 48 48'%3E%3Cg fill='none' stroke='%23C9A558' stroke-width='1.2'%3E%3Cpath d='M24 4 44 24 24 44 4 24Z'/%3E%3Cpath d='M24 14 34 24 24 34 14 24Z'/%3E%3Cpath d='M0 0h6M42 0h6M0 48h6M42 48h6'/%3E%3C/g%3E%3C/svg%3E");
  mask-image: linear-gradient(180deg, #000 0%, transparent 55%, #000 100%);
  -webkit-mask-image: linear-gradient(180deg, #000 0%, transparent 55%, #000 100%);
}

.afx-header { position: relative; z-index: 2; flex-shrink: 0;
  background: rgba(15,10,7,.55); backdrop-filter: blur(18px) saturate(1.2); -webkit-backdrop-filter: blur(18px) saturate(1.2);
  border-bottom: 1px solid rgba(201,165,88,.14); }
.afx-kente { height: 4px; background: repeating-linear-gradient(90deg,
  var(--gold) 0 26px, #111 26px 30px, var(--forest) 30px 46px, #111 46px 50px, var(--kred) 50px 62px, #111 62px 66px); opacity: .95; }
.afx-header-inner { display: flex; align-items: center; gap: 12px; max-width: 860px; margin: 0 auto; padding: 10px 16px; }
.afx-title { flex: 1; min-width: 0; }
.afx-name { font-family: 'Cinzel', serif; font-weight: 700; font-size: 19px; letter-spacing: .14em; color: var(--gold-hi); line-height: 1.1; }
.afx-status { display: flex; align-items: center; gap: 7px; font-size: 11.5px; color: var(--muted); margin-top: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.afx-dot { width: 7px; height: 7px; border-radius: 50%; background: #3DDC84; box-shadow: 0 0 0 3px rgba(61,220,132,.18); flex-shrink: 0; }
.afx-icon-btn { width: 40px; height: 40px; border-radius: 12px; display: grid; place-items: center; flex-shrink: 0;
  border: 1px solid rgba(201,165,88,.22); background: rgba(255,255,255,.04); color: var(--gold-hi); cursor: pointer; transition: background .2s, transform .2s; }
.afx-icon-btn:hover { background: rgba(201,165,88,.14); }
.afx-icon-btn:active { transform: scale(.94); }

.afx-avatar { position: relative; display: inline-block; border-radius: 50%; flex-shrink: 0; overflow: visible; }
.afx-avatar img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; object-position: top; display: block; position: relative; z-index: 1; border: 2px solid var(--earth); box-sizing: border-box; }
.afx-avatar-ring::before { content: ''; position: absolute; inset: -3px; border-radius: 50%;
  background: conic-gradient(from 0deg, var(--gold-hi), var(--terra), var(--forest), var(--gold), var(--gold-hi));
  animation: afxSpin 6s linear infinite; }

.afx-scroll { position: relative; z-index: 1; flex: 1; overflow-y: auto; overscroll-behavior: contain; -webkit-overflow-scrolling: touch; }
.afx-column { max-width: 860px; margin: 0 auto; padding: 22px 16px 28px; box-sizing: border-box; min-height: 100%; display: flex; flex-direction: column; }

.afx-hero { margin: auto 0; text-align: center; padding: 12px 0 8px; }
.afx-orb { position: relative; width: 112px; height: 112px; margin: 6px auto 22px; }
.afx-ripple { position: absolute; inset: 0; border-radius: 50%; border: 1px solid rgba(201,165,88,.5); animation: afxRipple 3.6s ease-out infinite; }
.afx-ripple-2 { animation-delay: 1.2s; } .afx-ripple-3 { animation-delay: 2.4s; }
.afx-kicker { font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: .32em; text-transform: uppercase; color: var(--gold); margin: 0 0 8px; }
.afx-akwaaba { font-family: 'Playfair Display', serif; font-style: italic; font-weight: 700; font-size: clamp(44px, 9vw, 72px); line-height: 1; margin: 0 0 14px;
  background: linear-gradient(100deg, var(--gold-hi) 0%, #fff4d6 35%, var(--gold) 60%, var(--terra) 100%); -webkit-background-clip: text; background-clip: text; color: transparent; }
.afx-lede { max-width: 520px; margin: 0 auto 26px; font-size: 15px; line-height: 1.65; color: #D9CBB5; text-wrap: pretty; }
.afx-lede strong { color: var(--gold-hi); font-weight: 600; }
.afx-cards { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; text-align: left; }
.afx-card { display: flex; align-items: center; gap: 12px; padding: 14px; border-radius: 18px; cursor: pointer; text-align: left; line-height: 1.3;
  background: linear-gradient(160deg, rgba(255,248,235,.07), rgba(255,248,235,.025));
  border: 1px solid rgba(201,165,88,.18); color: var(--cream); font: inherit;
  backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px);
  transition: transform .25s ease, border-color .25s ease, background .25s ease; opacity: 0; animation: afxRise .5s ease-out forwards; }
.afx-card:hover { transform: translateY(-3px); border-color: rgba(201,165,88,.55); background: linear-gradient(160deg, rgba(201,165,88,.16), rgba(255,248,235,.04)); }
.afx-card:active { transform: scale(.98); }
.afx-card-icon { width: 42px; height: 42px; border-radius: 13px; display: grid; place-items: center; flex-shrink: 0; color: #fff; }
.afx-tone-0 { background: linear-gradient(135deg, #D9B463, #8B6914); color: #1C1308; }
.afx-tone-1 { background: linear-gradient(135deg, #C8663A, #7E2F18); }
.afx-tone-2 { background: linear-gradient(135deg, #2E9A63, #134D30); }
.afx-card-text { display: flex; flex-direction: column; align-items: flex-start; min-width: 0; text-align: left; }
.afx-card-title { font-weight: 600; font-size: 14px; color: #FFF6E6; }
.afx-card-hint { font-size: 12px; line-height: 1.35; color: var(--muted); margin-top: 3px; }

.afx-thread { display: flex; flex-direction: column; gap: 14px; }
.afx-row { display: flex; gap: 10px; align-items: flex-end; animation: afxRise .3s ease-out; }
.afx-row-user { justify-content: flex-end; }
.afx-bubble { max-width: min(82%, 600px); padding: 12px 16px; font-size: 15px; line-height: 1.65; word-wrap: break-word; }
.afx-bubble p { margin: 0 0 10px; } .afx-bubble p:last-child, .afx-bubble .afx-list:last-child { margin-bottom: 0; }
.afx-bubble strong { color: var(--gold-hi); font-weight: 600; }
.afx-bubble-user { background: linear-gradient(135deg, #F1D58E, #C9A558 60%, #A9842F); color: #1C1308; border-radius: 22px 22px 6px 22px; font-weight: 500; white-space: pre-wrap; box-shadow: 0 8px 24px rgba(201,165,88,.18); }
.afx-bubble-afia { position: relative; background: rgba(255,248,235,.06); border: 1px solid rgba(201,165,88,.16); border-radius: 22px 22px 22px 6px; color: #F3E9D8;
  backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); }
.afx-bubble-afia::before { content: ''; position: absolute; left: 14px; right: 14px; top: -1px; height: 2px; border-radius: 2px;
  background: linear-gradient(90deg, var(--gold), var(--forest), var(--kred)); opacity: .55; }
.afx-streaming p:last-child::after { content: ''; display: inline-block; width: 7px; height: 15px; margin-left: 3px; vertical-align: -2px; border-radius: 2px; background: var(--gold); animation: afxBlink 1s steps(2) infinite; }
.afx-h { font-family: 'Cinzel', serif; font-weight: 700; letter-spacing: .06em; color: var(--gold-hi); font-size: 14px; }
.afx-list { margin: 0 0 10px; padding-left: 20px; } .afx-list li { margin: 4px 0; } .afx-list li::marker { color: var(--gold); }
.afx-notice p { margin-bottom: 12px; }
.afx-pill { border: none; border-radius: 999px; padding: 10px 18px; cursor: pointer; font: 600 13px 'Montserrat', sans-serif; letter-spacing: .04em;
  background: linear-gradient(135deg, var(--gold-hi), var(--gold)); color: #1C1308; }
.afx-pill-ghost { background: transparent; color: var(--gold-hi); border: 1px solid rgba(201,165,88,.5); }
.afx-typing { display: inline-flex; gap: 6px; padding: 15px 18px; }
.afx-typing span { width: 8px; height: 8px; border-radius: 50%; animation: afxBounce 1.2s ease-in-out infinite; }
.afx-typing span:nth-child(1) { background: var(--gold); }
.afx-typing span:nth-child(2) { background: #2E9A63; animation-delay: .15s; }
.afx-typing span:nth-child(3) { background: #C8463A; animation-delay: .3s; }

.afx-footer { position: relative; z-index: 2; flex-shrink: 0; padding: 10px 16px calc(12px + env(safe-area-inset-bottom));
  background: linear-gradient(180deg, transparent, rgba(15,10,7,.85) 35%); }
.afx-composer { max-width: 860px; margin: 0 auto; display: flex; align-items: flex-end; gap: 8px; padding: 7px 7px 7px 18px; border-radius: 28px;
  background: rgba(255,248,235,.07); border: 1px solid rgba(201,165,88,.28); box-shadow: 0 12px 40px rgba(0,0,0,.4);
  backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); transition: border-color .2s, box-shadow .2s; }
.afx-composer:focus-within { border-color: rgba(241,213,142,.7); box-shadow: 0 0 0 4px rgba(201,165,88,.14), 0 12px 40px rgba(0,0,0,.4); }
.afx-composer textarea { flex: 1; resize: none; border: none; outline: none; background: transparent; color: var(--cream);
  font: 16px/1.5 'Montserrat', sans-serif; padding: 9px 0; max-height: 150px; }
.afx-composer textarea::placeholder { color: #8F7E68; }
.afx-send { width: 44px; height: 44px; border-radius: 50%; border: none; display: grid; place-items: center; flex-shrink: 0; cursor: pointer;
  background: linear-gradient(135deg, var(--gold-hi), var(--gold) 55%, var(--gold-lo)); color: #1C1308; box-shadow: 0 6px 18px rgba(201,165,88,.35); transition: transform .2s, opacity .2s; }
.afx-send:hover:not(:disabled) { transform: scale(1.06); }
.afx-send:disabled { opacity: .35; cursor: default; box-shadow: none; }
.afx-fineprint { text-align: center; font-size: 11px; color: #8F7E68; margin: 8px 0 0; }
.afx-fineprint button { background: none; border: none; padding: 0; color: var(--gold); font: inherit; text-decoration: underline; text-underline-offset: 2px; cursor: pointer; }

@keyframes afxIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes afxRise { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
@keyframes afxSpin { to { transform: rotate(360deg); } }
@keyframes afxRipple { from { transform: scale(1); opacity: .7; } to { transform: scale(1.9); opacity: 0; } }
@keyframes afxDrift { from { transform: translate3d(0,0,0) scale(1); } to { transform: translate3d(-3%,2%,0) scale(1.06); } }
@keyframes afxBounce { 0%, 80%, 100% { transform: translateY(0); opacity: .5; } 40% { transform: translateY(-6px); opacity: 1; } }
@keyframes afxBlink { to { opacity: 0; } }

@media (max-width: 720px) {
  .afx-column { padding: 16px 14px 20px; }
  .afx-hero { padding-top: 4px; }
  .afx-cards { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 9px; }
  .afx-card { flex-direction: column; align-items: flex-start; gap: 9px; padding: 12px; border-radius: 16px; }
  .afx-card-icon { width: 34px; height: 34px; border-radius: 11px; }
  .afx-card-icon svg { width: 19px; height: 19px; }
  .afx-card-title { font-size: 13.5px; }
  .afx-card-hint { font-size: 11.5px; }
  .afx-bubble { max-width: 86%; font-size: 15px; }
  .afx-akwaaba { font-size: 46px; margin-bottom: 10px; }
  .afx-kicker { font-size: 9.5px; letter-spacing: .26em; }
  .afx-lede { font-size: 14px; line-height: 1.55; margin-bottom: 18px; }
  .afx-orb { width: 84px; height: 84px; margin: 2px auto 16px; }
  .afx-orb .afx-avatar { width: 84px !important; height: 84px !important; }
}
@media (prefers-reduced-motion: reduce) {
  .afx-root, .afx-card, .afx-row { animation: none; opacity: 1; }
  .afx-bg::before, .afx-avatar-ring::before, .afx-ripple, .afx-typing span { animation: none; }
}
`;
